import { yupResolver } from '@hookform/resolvers/yup';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { CompanyBasic } from '~hooks/useCompany/models';
import { KMLFile } from '~hooks/useKMLFile/models';
import { useKMLFile } from '~hooks/useKMLFile/useKML';
import fetchAllPages from '~utils/fetchAllPages';

import { MapFileEditDialog } from './MapFileEditDialog';

const selectedCompanySchema = yup.object().shape({
  company: yup.mixed<CompanyBasic>().nullable(),
});
type SelectedCompanyDTO = yup.InferType<typeof selectedCompanySchema>;

const MapFiles = () => {
  const [kmlFiles, setKmlFiles] = useState<KMLFile[]>([]);
  const [isFetchingKMLFiles, setIsFetchingKMLFiles] = useState(false);
  const { fetchKMLFiles, createKMLFile, isUploading, deleteKMLFile } = useKMLFile();
  const [selectedFile, setSelectedFile] = useState<KMLFile | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<SelectedCompanyDTO>({
    resolver: yupResolver(selectedCompanySchema),
    mode: 'onSubmit',
    defaultValues: { company: null },
  });

  const companyId = form.watch('company')?.id ?? null;

  // Add file input ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Add file upload handler
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !companyId) return;

    const uploadedKMLFile = await createKMLFile(companyId, file);
    if (uploadedKMLFile) {
      setKmlFiles((prevFiles) => [uploadedKMLFile, ...prevFiles]);
    }

    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Load any KML files
  const loadKMLFiles = async (companyId: string, signal: AbortSignal) => {
    setIsFetchingKMLFiles(true);
    setKmlFiles([]);
    const files = await fetchAllPages(async (cursor, limit) => {
      try {
        return await fetchKMLFiles({ companyId, cursor, limit, signal });
      } catch {
        return undefined;
      }
    });

    if (signal.aborted) {
      return;
    }

    setKmlFiles(files);
    setIsFetchingKMLFiles(false);
  };

  useEffect(() => {
    const controller = new AbortController();

    if (companyId) {
      loadKMLFiles(companyId, controller.signal);
    } else {
      setKmlFiles([]);
      setIsFetchingKMLFiles(false);
    }

    return () => controller.abort();
  }, [companyId]);

  const handleUpdateFile = (updatedFile: KMLFile) => {
    if (updatedFile) {
      setKmlFiles((prevFiles) =>
        prevFiles.map((file) => (file.id === updatedFile.id ? updatedFile : file)),
      );
    }
  };

  const handleDelete = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (companyId && selectedFile) {
      try {
        setIsLoading(true);
        await deleteKMLFile(companyId, selectedFile);
        setKmlFiles((prevFiles) => prevFiles.filter((f) => f.id !== selectedFile.id));
      } catch (error) {
        console.error(error);
      } finally {
        setSelectedFile(null);
        setIsLoading(false);
        setIsDeleteDialogOpen(false);
      }
    }
  }, [selectedFile]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <CompanySelectorFormField
          clearable
          control={form.control}
          errors={form.formState.errors}
          hideLabel
          inputProps={{ placeholder: `${t('form_fields.company_selector_hint')}` }}
          name="company"
          sx={{ width: '540px' }}
        />

        {companyId && (
          <Box>
            <input
              type="file"
              accept=".kml,.kmz"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              ref={fileInputRef}
            />
            <Button
              loading={isFetchingKMLFiles || isUploading}
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              {t('administration.map_files.upload_file')}
            </Button>
          </Box>
        )}
      </Box>
      {isFetchingKMLFiles && (
        <Box
          sx={{
            mt: 2,
          }}
        >
          <LoadingSpinner loadingIndicatorSize={16} isVisible={isFetchingKMLFiles} />
        </Box>
      )}
      {!isFetchingKMLFiles && kmlFiles.length === 0 && companyId && (
        <Box
          sx={{
            mt: 2,
          }}
        >
          <Typography>{t('administration.map_files.no_files_found')}</Typography>
        </Box>
      )}
      {!isFetchingKMLFiles && kmlFiles.length > 0 && (
        <Box sx={{ pt: 2 }}>
          {/* Header */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 3fr 96px',
              gap: 2,
              backgroundColor: (theme) => theme.semantic.surfaceSunken,
              p: 1.5,
              borderRadius: (theme) => `${theme.spacing(0.5)} ${theme.spacing(0.5)} 0 0`,
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
              borderRight: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
              {t('administration.map_files.file_name')}
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
              {t('administration.map_files.file')}
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, textAlign: 'right' }}>
              {t('actions.actions')}
            </Typography>
          </Box>

          {/* Rows */}
          {kmlFiles.map((file) => (
            <Box
              key={file.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 3fr 96px',
                gap: 2,
                alignContent: 'center',
                alignItems: 'center',
                height: '48px',
                p: 1.5,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
                borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontStyle: !file.name ? 'italic' : undefined,
                }}
              >
                {file.name || t('administration.map_files.unnamed_file')}
              </Typography>
              <PseudoLink
                title={file.fileName}
                action={() => window.open(file.fileUrl)}
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedFile(file);
                    setIsDialogOpen(true);
                  }}
                >
                  <Edit />
                </IconButton>
                <SimpleMenu
                  options={[
                    {
                      title: t('actions.delete'),
                      icon: <Delete />,
                      callBack: () => {
                        setSelectedFile(file);
                        handleDelete();
                      },
                    },
                  ]}
                >
                  <MoreHoriz />
                </SimpleMenu>
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {companyId && (
        <MapFileEditDialog
          companyId={companyId}
          open={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false);
            setSelectedFile(null);
          }}
          onSuccess={async (data) => {
            await handleUpdateFile(data);
            setIsDialogOpen(false);
            setSelectedFile(null);
          }}
          defaultKMLFile={selectedFile}
        />
      )}
      <ConfirmationDialog
        isBusy={isLoading}
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedFile(null);
        }}
        onConfirm={handleConfirmDelete}
        title={t('administration.map_files.delete_file_title')}
      >
        <Typography>{t('administration.map_files.delete_file_description')}</Typography>
      </ConfirmationDialog>
    </Box>
  );
};

export default MapFiles;
